import { MessageSquare, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { CallLog } from "../services/callLogsApi";

interface CallLogsTableProps {
  callLogs: CallLog[];
  deletingId?: string;
  onEdit: (callLog: CallLog) => void;
  onDelete: (callLog: CallLog) => void;
  onSendMessage?: (callLog: CallLog) => void;
}

const getPriorityBadgeVariant = (
  priorityName?: string
): "default" | "secondary" | "outline" | "destructive" => {
  const priority = (priorityName ?? "").toLowerCase();

  if (priority === "urgent") {
    return "destructive";
  }

  if (priority === "high") {
    return "default";
  }

  if (priority === "medium") {
    return "secondary";
  }

  return "outline";
};

const formatDate = (value?: string) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString();
};

export const CallLogsTable = ({
  callLogs,
  deletingId,
  onEdit,
  onDelete,
  onSendMessage,
}: CallLogsTableProps) => {
  if (callLogs.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
        No call logs found.
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Caller</TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Inquiry Date</TableHead>
            <TableHead>Follow-up Date</TableHead>
            <TableHead>Assigned To</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {callLogs.map((callLog) => (
            <TableRow key={callLog.id}>
              <TableCell className="font-medium">{callLog.callerName}</TableCell>
              <TableCell>{callLog.phone || "-"}</TableCell>
              <TableCell>{callLog.subject}</TableCell>
              <TableCell>
                <Badge variant="outline">{callLog.statusName || callLog.status}</Badge>
              </TableCell>
              <TableCell>
                <Badge variant={getPriorityBadgeVariant(callLog.priorityName)}>
                  {callLog.priorityName || callLog.priority}
                </Badge>
              </TableCell>
              <TableCell>{formatDate(callLog.inquiryDate)}</TableCell>
              <TableCell>{formatDate(callLog.followUpDate)}</TableCell>
              <TableCell>{callLog.assignedToUserName || "-"}</TableCell>
              <TableCell>{callLog.customerName || "-"}</TableCell>
              <TableCell className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {callLog.customerId && onSendMessage && <DropdownMenuItem onClick={() => onSendMessage(callLog)}>
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Send message
                    </DropdownMenuItem>}
                    <DropdownMenuItem onClick={() => onEdit(callLog)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      disabled={deletingId === callLog.id}
                      onClick={() => onDelete(callLog)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {deletingId === callLog.id ? "Deleting..." : "Delete"}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
